import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle, roundedPoly, shade } from './draw-utils';
import type { ProceduralDef } from '../types';

const PLATE = 0x3a3548; // dark warden steel

/**
 * B5 Warden parts — docs/02-bosses.md. Finale boss.
 * Body frame 0: sealed armor (invulnerable), frame 1: exposed glowing core.
 */
export const bossWardenBody: ProceduralDef = {
  key: 'boss/warden/body',
  width: 224,
  height: 224,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 2,
  draw(ctx, w, h, frame) {
    const c = w / 2;
    const R = c - 16;
    const exposed = frame === 1;

    // Octagonal fortress hull
    const pts: Array<[number, number]> = [];
    for (let i = 0; i < 8; i++) {
      const a = (Math.PI / 4) * i + Math.PI / 8;
      pts.push([c + Math.cos(a) * R, c + Math.sin(a) * R]);
    }
    roundedPoly(ctx, pts, 14);
    fillOutlined(ctx, PLATE, 9);

    // Plate seams
    ctx.strokeStyle = css(shade(PLATE, 0.6));
    ctx.lineWidth = 5;
    for (let i = 0; i < 8; i++) {
      const a = (Math.PI / 4) * i + Math.PI / 8;
      ctx.beginPath();
      ctx.moveTo(c + Math.cos(a) * R * 0.45, c + Math.sin(a) * R * 0.45);
      ctx.lineTo(c + Math.cos(a) * R * 0.9, c + Math.sin(a) * R * 0.9);
      ctx.stroke();
    }

    // Heavy bolts
    ctx.fillStyle = css(shade(PLATE, 0.5));
    for (const [x, y] of pts) {
      circle(ctx, c + (x - c) * 0.84, c + (y - c) * 0.84, 6);
      ctx.fill();
    }

    if (exposed) {
      // Blown-open hatch: glowing core + cracked rim
      circle(ctx, c, c, R * 0.5);
      ctx.fillStyle = 'rgba(232, 84, 47, 0.35)';
      ctx.fill();
      circle(ctx, c, c, R * 0.4);
      fillOutlined(ctx, COLORS.dangerOrange, 6);
      circle(ctx, c, c, R * 0.24);
      ctx.fillStyle = css(shade(COLORS.dangerOrange, 1.4));
      ctx.fill();
      circle(ctx, c - R * 0.08, c - R * 0.1, R * 0.08);
      ctx.fillStyle = 'rgba(255, 244, 230, 0.95)';
      ctx.fill();
      ctx.strokeStyle = css(COLORS.outline);
      ctx.lineWidth = 4;
      for (let i = 0; i < 5; i++) {
        const a = (Math.PI * 2 * i) / 5 + 0.4;
        ctx.beginPath();
        ctx.moveTo(c + Math.cos(a) * R * 0.42, c + Math.sin(a) * R * 0.42);
        ctx.lineTo(c + Math.cos(a) * R * 0.62, c + Math.sin(a) * R * 0.62);
        ctx.stroke();
      }
    } else {
      // Sealed visor slit — menacing but shut
      ctx.beginPath();
      ctx.roundRect(c - R * 0.42, c - 9, R * 0.84, 18, 9);
      fillOutlined(ctx, shade(PLATE, 0.45), 5);
      ctx.beginPath();
      ctx.roundRect(c - R * 0.3, c - 3.5, R * 0.6, 7, 3.5);
      ctx.fillStyle = css(COLORS.dangerOrange);
      ctx.fill();
    }
    void h;
  },
};

/** Shoulder turret — the real HP of phase 1. Barrel points +X. */
export const bossWardenTurret: ProceduralDef = {
  key: 'boss/warden/turret',
  width: 64,
  height: 64,
  anchorX: 0.45,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const cx = w * 0.45;
    const cy = h / 2;

    // Base
    circle(ctx, cx, cy, 18);
    fillOutlined(ctx, PLATE, 5);
    circle(ctx, cx - 4, cy - 4, 7);
    ctx.fillStyle = css(shade(PLATE, 1.25));
    ctx.fill();

    // Barrel
    ctx.beginPath();
    ctx.roundRect(cx + 8, cy - 5, 22, 10, 4);
    fillOutlined(ctx, shade(PLATE, 0.75), 4);

    // Sight light
    circle(ctx, cx + 4, cy, 4.5);
    ctx.fillStyle = css(COLORS.dangerRed);
    ctx.fill();
    void h;
  },
};
